import axiosInstance from '../index';
import db from '../db';

const formatDate = (date) => {
  if (typeof date === 'string') {
    try {
      return new Date(date).toISOString();
    } catch (e) {
      return new Date().toISOString();
    }
  }
  return date.toISOString();
};

export const syncData = async (handleLogout, updateSessionsState, updateProgressState) => {
  const sync = async () => {
    try {
      const dirtyProgress = await db.chapterProgress.where('dirty').equals(1).toArray();
      const dirtySessions = await db.sessions.where('dirty').equals(1).toArray();
      const lastSyncAt = localStorage.getItem('lastSyncAt') || new Date(0).toISOString();

      const requestData = {
        last_sync_at: formatDate(lastSyncAt),
        chapter_progress: dirtyProgress.map((p) => ({
          chapter_id: p.chapter_id,
          status: p.status,
          progress_percent: p.progress_percent,
          last_updated_at: formatDate(p.last_updated_at)
        })),
        sessions: dirtySessions.map((s) => ({
          start_chapter: s.startChapter,
          end_chapter: s.endChapter,
          last_listened_at: formatDate(s.last_listened_at)
        }))
      };

      const response = await axiosInstance.post('/api/sync', requestData);

      // Update local data with server changes
      const serverChanges = response.data;
      await db.transaction('rw', db.chapterProgress, db.sessions, async () => {
        // Handle chapter progress
        for (const progress of serverChanges.chapter_progress) {
          const local = await db.chapterProgress.get(progress.chapter_id);
          if (!local || new Date(local.last_updated_at) < new Date(progress.last_updated_at)) {
            await db.chapterProgress.put({ ...progress, dirty: 0 });
          }
        }

        // Handle sessions
        for (const session of serverChanges.sessions) {
          await db.sessions.put({
            ...session,
            startChapter: session.start_chapter,
            endChapter: session.end_chapter,
            last_listened_at: session.last_listened_at,
            dirty: 0
          });
        }

        // Clear dirty flags for synced items
        await Promise.all([
          ...dirtyProgress.map(progress => 
            db.chapterProgress.update(progress.chapter_id, { dirty: 0 })
          ),
          ...dirtySessions.map(session =>
            db.sessions.update(session.id, { dirty: 0 })
          )
        ]);
      });

      localStorage.setItem('lastSyncAt', new Date().toISOString());

      // Update UI state after sync if we received data from server
      if (updateSessionsState && serverChanges.sessions?.length > 0) {
        const freshSessions = await db.sessions.orderBy('last_listened_at').reverse().limit(10).toArray();
        updateSessionsState(freshSessions);
      }

      if (updateProgressState && serverChanges.chapter_progress?.length > 0) {
        const freshProgress = await db.chapterProgress.toArray();
        const formattedProgress = freshProgress.reduce(
          (acc, p) => ({ 
            ...acc, 
            [p.chapter_id]: { status: p.status, progress_percent: p.progress_percent } 
          }), 
          {}
        );
        updateProgressState(formattedProgress);
      }

    } catch (error) {
      if (error.offline) {
        console.log('Device is offline, sync will retry when online');
        return;
      }
      
      console.error('Sync failed:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  // Initial sync
  if (navigator.onLine) {
    await sync();
  }

  // Set up sync triggers
  let syncDebounceTimeout;
  const debouncedSync = () => {
    clearTimeout(syncDebounceTimeout);
    syncDebounceTimeout = setTimeout(sync, 5000);
  };

  // Sync when coming back online
  window.addEventListener('online', sync);

  // Sync when database changes
  db.chapterProgress.hook('creating', debouncedSync);
  db.chapterProgress.hook('updating', debouncedSync);
  db.sessions.hook('creating', debouncedSync);
  db.sessions.hook('updating', debouncedSync);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', sync);
    clearTimeout(syncDebounceTimeout);
  };
};